import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Feather, WifiOff } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { useOffline } from "../../hooks/usePWA";

export function TripChatbot({ tripId, trialRemaining = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Bonjour ! Je suis votre conseiller voyage dédié à cet itinéraire. Posez-moi n'importe quelle question sur votre voyage, les activités, les vols, la culture locale..." }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const storageKey = `voyager_chat_session_${tripId}`;
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(storageKey));
  const [remaining, setRemaining] = useState(trialRemaining);
  const limitReached = remaining !== null && remaining <= 0;
  const messagesEndRef = useRef(null);
  const isOffline = useOffline();

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/trips/${tripId}/chat/history`, { params: { session_id: sessionId } })
      .then(({ data }) => {
        if (data.messages?.length) {
          setMessages((prev) => [prev[0], ...data.messages.map((m) => ({ role: m.role === "user" ? "user" : "bot", text: m.content }))]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const sendMessage = async () => {
    if (!input.trim() || sending || limitReached) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/trips/${tripId}/chat`,
        { message: userMsg, session_id: sessionId }
      );
      setSessionId(data.session_id);
      localStorage.setItem(storageKey, data.session_id);
      if (data.trial_remaining != null) setRemaining(data.trial_remaining);
      setMessages((prev) => [...prev, { role: "bot", text: data.response }]);
    } catch (err) {
      if (err.response?.status === 403 && remaining !== null) setRemaining(0);
      const text = [403, 500].includes(err.response?.status) ? err.response.data?.detail : "Désolé, une erreur s'est produite. Veuillez réessayer.";
      setMessages((prev) => [...prev, { role: "bot", text }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#C17F59] text-[#F9F6F0] shadow-lg z-50 flex items-center justify-center transition-all hover:bg-[#A86A46]"
        style={{ borderRadius: "50%", boxShadow: "0 4px 20px rgba(193,127,89,0.4)" }}
        data-testid="chatbot-toggle-btn"
      >
        {open ? <X size={20} strokeWidth={1.5} /> : <MessageCircle size={20} strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 w-80 sm:w-96 chatbot-panel z-50 animate-fade-in-up"
          style={{ maxHeight: "70vh", display: "flex", flexDirection: "column" }}
          data-testid="chatbot-panel"
        >
          <div className="p-4 border-b border-[#DCD4C6] flex items-center justify-between">
            <div>
              <h4 className="text-base" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
                Votre conseiller voyage
              </h4>
              <p className="text-xs text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>Expert dédié à votre destination</p>
            </div>
            <Feather size={16} strokeWidth={1.5} className="text-[#C17F59]" />
          </div>

          {/* Mode hors connexion */}
          {isOffline ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-4">
              <WifiOff size={28} strokeWidth={1.5} className="text-[#DCD4C6]" />
              <div>
                <p className="text-sm font-semibold text-[#2B2623] mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  Connexion requise
                </p>
                <p className="text-xs text-[#9A8F86] italic leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  Le Conseiller voyage IA nécessite une connexion internet. Votre itinéraire reste consultable hors connexion.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={msg.role === "user" ? "chat-bubble-user" : "chat-bubble-bot"}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="chat-bubble-bot flex items-center gap-1">
                      <span className="animate-pulse" style={{ fontFamily: "'Lora', Georgia, serif" }}>...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {remaining !== null && (
                <p className="px-3 pt-2 text-xs text-[#8B5E34] italic" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="chatbot-trial-remaining">
                  {limitReached ? "Essai terminé : débloquez votre voyage pour continuer." : `Essai gratuit : ${remaining} message${remaining > 1 ? "s" : ""} restant${remaining > 1 ? "s" : ""}`}
                </p>
              )}
              <div className="p-3 border-t border-[#DCD4C6] flex items-end gap-2">
                <textarea
                  className="flex-1 bg-[#F9F6F0] border border-[#DCD4C6] text-sm p-2 resize-none focus:outline-none focus:border-[#8B5E34] rounded-none"
                  rows={2}
                  placeholder="Posez votre question..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={limitReached}
                  style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending || limitReached}
                  className="w-9 h-9 bg-[#C17F59] text-[#F9F6F0] flex items-center justify-center hover:bg-[#A86A46] disabled:opacity-50 transition-colors flex-shrink-0"
                  style={{ borderRadius: "1px" }}
                  data-testid="chatbot-send-btn"
                >
                  <Send size={14} strokeWidth={1.5} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
